import React, { useEffect, useState } from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, Tooltip,
} from 'recharts';
import { Loader2, MessageSquareQuote } from 'lucide-react';
import { atlasApi, AtlasUser, FeedbackRelatorio } from '../shared/atlasApi';

interface Props {
  user: AtlasUser;
  cicloId: string;
  funccodi: number;
}

const RELACAO_LABEL: Record<string, string> = {
  gestor: 'Gestor', par: 'Pares', subordinado: 'Subordinados', autoavaliacao: 'Autoavaliação',
};

const Relatorio: React.FC<Props> = ({ user, cicloId, funccodi }) => {
  const [rel, setRel] = useState<FeedbackRelatorio | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    atlasApi.relatorioFeedback(user, cicloId, funccodi)
      .then(r => { if (!cancel) setRel(r); })
      .catch(e => { if (!cancel) setErro(e.message || 'Erro ao carregar relatório'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user, cicloId, funccodi]);

  if (loading) return <div className="p-10 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando relatório...</div>;
  if (erro) return <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-700 max-w-2xl">{erro}</div>;
  if (!rel) return null;

  const radarData = (rel.radar || []).map(r => ({
    categoria: r.categoria,
    'Autoavaliação': r.autoavaliacao ?? 0,
    'Outros': r.outros ?? 0,
  }));

  const comentarios = rel.por_pergunta.filter(p => p.tipo === 'texto_livre' && (p.comentarios || []).length > 0);

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Relatório de feedback — {rel.ciclo.titulo}</div>
        <h2 className="text-xl font-bold text-gray-900">{rel.avaliado.nome || `Colaborador #${rel.avaliado.funccodi}`}</h2>
        <p className="text-sm text-gray-500">{rel.avaliado.cargo || '—'} · {rel.total_respostas} resposta(s)</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Radar auto vs outros */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2">Por categoria</h3>
          {radarData.length === 0 ? (
            <p className="text-sm text-gray-400 p-6 text-center">Sem perguntas de escala.</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={radarData} outerRadius="70%">
                <PolarGrid />
                <PolarAngleAxis dataKey="categoria" tick={{ fontSize: 12 }} />
                <PolarRadiusAxis domain={[0, 5]} tick={{ fontSize: 10 }} />
                <Radar name="Autoavaliação" dataKey="Autoavaliação" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.25} />
                <Radar name="Outros" dataKey="Outros" stroke="#10b981" fill="#10b981" fillOpacity={0.2} />
                <Legend />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Por tipo de relação */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Médias por relação</h3>
          <div className="grid grid-cols-2 gap-3">
            {['gestor', 'par', 'subordinado', 'autoavaliacao'].map(rk => {
              const v = rel.por_tipo_relacao[rk];
              return (
                <div key={rk} className="rounded-xl border border-gray-100 p-3">
                  <div className="text-[11px] text-gray-400 uppercase tracking-wider">{RELACAO_LABEL[rk]}</div>
                  <div className="text-2xl font-bold text-gray-900">{v && v.media != null ? v.media.toFixed(1) : '—'}</div>
                  <div className="text-[11px] text-gray-400">{v ? `${v.total} resp.` : 'sem dados'}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Perguntas escala/sim-nao */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Por pergunta</h3>
        <ul className="space-y-2">
          {rel.por_pergunta.filter(p => p.tipo !== 'texto_livre').map(p => (
            <li key={p.pergunta_id} className="flex items-center gap-3 text-sm">
              <span className="flex-1 text-gray-700">{p.texto}</span>
              {p.tipo === 'escala_1_5' && <span className="font-semibold text-gray-900">{p.media != null ? `${p.media}/5` : '—'}</span>}
              {p.tipo === 'sim_nao' && <span className="text-gray-600">Sim {p.distribuicao?.sim || 0} · Não {p.distribuicao?.nao || 0}</span>}
            </li>
          ))}
        </ul>
      </div>

      {/* Comentários */}
      {comentarios.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2"><MessageSquareQuote className="w-4 h-4 text-gray-400" /> Comentários (anônimos)</h3>
          {comentarios.map(p => (
            <div key={p.pergunta_id} className="mb-3">
              <div className="text-xs text-gray-400 mb-1">{p.texto}</div>
              <ul className="space-y-1">
                {(p.comentarios || []).map((c, i) => (
                  <li key={i} className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">"{c}"</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Relatorio;
